import { buildAumProductMap, getAumProductCode } from '@/hooks/usePricing';
import {
  inr,
  type ProductDiscount,
  type ProductInfo,
  type SetupQuoteResult,
} from '@/lib/sales-metrics';
import {
  formatProductLabel,
  getDefaultTermYears,
  resolveLineDiscountAmount,
  resolveProductLineTotal,
} from '@/utils/productTermPricing';

export interface WizardQuotationItem {
  name: string;
  price: number;
  code?: string;
}

export interface WizardQuotationInput {
  catalog: ProductInfo[];
  selectedProducts: string[];
  customPrices: Record<string, number>;
  productTerms: Record<string, number>;
  setupQuote: SetupQuoteResult | null;
  renewalDiscountPercent: number;
}

export interface WizardBillingTotals {
  preDiscountSubtotal: number;
  subtotal: number;
  gstAmount: number;
  cgst: number;
  sgst: number;
  total: number;
  discountLabel: string;
}

export function buildWizardQuotationItems({
  catalog,
  selectedProducts,
  customPrices,
  productTerms,
  setupQuote,
  renewalDiscountPercent,
}: WizardQuotationInput): WizardQuotationItem[] {
  const aumMap = buildAumProductMap(catalog);
  const flatItems = catalog.filter(
    (p) => selectedProducts.includes(p.code) && !aumMap[p.code] && p.code !== 'ADD_ARN',
  );
  const aumCode = getAumProductCode(selectedProducts, aumMap);
  const aumItem = aumCode ? catalog.find((p) => p.code === aumCode) : undefined;

  const items: WizardQuotationItem[] = [];

  if (aumItem && setupQuote?.setupAmount != null) {
    const bandSuffix = setupQuote.slab?.band ? ` (${setupQuote.slab.band})` : '';
    items.push({
      name: `${formatProductLabel(aumItem.name, aumItem.code, productTerms)}${bandSuffix}`,
      price: setupQuote.setupAmount,
      code: aumItem.code,
    });
  }

  flatItems.forEach((p) => {
    items.push({
      name: formatProductLabel(p.name, p.code, productTerms),
      price: resolveProductLineTotal(
        p.code,
        p.price,
        customPrices,
        productTerms,
        renewalDiscountPercent,
      ),
      code: p.code,
    });
  });

  if (setupQuote && setupQuote.additionalArnAmount > 0) {
    items.push({
      name: `Additional ARN x${setupQuote.additionalArnCount}`,
      price: setupQuote.additionalArnAmount,
    });
  }

  return items;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function buildWizardBillingTotals(
  quotationItems: WizardQuotationItem[],
  setupQuote: SetupQuoteResult | null,
  gstRateFallback = 18,
): WizardBillingTotals {
  const preDiscountSubtotal = quotationItems.reduce((sum, item) => sum + item.price, 0);
  // Per-product discounts (ADR-019) are summed server-side into discountAmount,
  // so a single aggregate "Discount" line keeps the document reconciling.
  const discountLabel = 'Discount';

  const subtotal = setupQuote?.subtotal ?? preDiscountSubtotal;
  const gstAmount = setupQuote?.gstAmount ?? round2((subtotal * gstRateFallback) / 100);
  const cgst = gstAmount / 2;
  const sgst = cgst;
  const total = setupQuote?.grandTotal ?? round2(subtotal + gstAmount);

  return {
    preDiscountSubtotal,
    subtotal,
    gstAmount,
    cgst,
    sgst,
    total,
    discountLabel,
  };
}

export function buildWizardQuotationWithDiscount({
  catalog,
  selectedProducts,
  customPrices,
  productTerms,
  setupQuote,
  renewalDiscountPercent,
}: WizardQuotationInput): {
  items: WizardQuotationItem[];
  totals: WizardBillingTotals;
} {
  const items = buildWizardQuotationItems({
    catalog,
    selectedProducts,
    customPrices,
    productTerms,
    setupQuote,
    renewalDiscountPercent,
  });

  const totals = buildWizardBillingTotals(items, setupQuote, setupQuote?.gstRate ?? 18);

  if (setupQuote && setupQuote.discountAmount > 0) {
    items.push({
      name: totals.discountLabel,
      price: -setupQuote.discountAmount,
    });
  }

  return { items, totals };
}

// One detailed quotation row, mirroring the email's
// `Product | Duration | Price | Discount | Net Amount` table (ADR-020).
export interface DetailedQuotationRow {
  name: string;
  duration: string;
  price: number;
  discountLabel: string;
  discountAmount: number;
  net: number;
}

export interface DetailedQuotationTotals {
  grossSubtotal: number;
  totalDiscount: number;
  netSubtotal: number;
  gstPercentage: number;
  gstAmount: number;
  totalPayable: number;
}

export interface DetailedQuotationInput extends WizardQuotationInput {
  productDiscounts: Record<string, ProductDiscount>;
}

export interface DetailedQuotation {
  rows: DetailedQuotationRow[];
  totals: DetailedQuotationTotals;
  // True when an AUM-based product is on the quote, so the caller can show the
  // "Current AUM" note before the table (matching the email).
  hasAum: boolean;
}

const durationText = (years: number): string => {
  const y = Math.max(1, Math.floor(years));
  return `${y} year${y > 1 ? 's' : ''}`;
};

// Builds the same detailed, per-product quotation the email renders server-side
// (renderQuotationTableHtml) - product rows with duration + per-line discount +
// net, plus aligned Sub-Total / GST / Total Payable. Totals come from the
// server-authoritative `setupQuote` so the dialog never disagrees with the sent
// email.
export function buildDetailedQuotation({
  catalog,
  selectedProducts,
  customPrices,
  productTerms,
  productDiscounts,
  setupQuote,
  renewalDiscountPercent,
}: DetailedQuotationInput): DetailedQuotation {
  const aumMap = buildAumProductMap(catalog);
  const aumCode = getAumProductCode(selectedProducts, aumMap);
  const aumItem = aumCode ? catalog.find((p) => p.code === aumCode) : undefined;
  const flatItems = catalog.filter(
    (p) => selectedProducts.includes(p.code) && !aumMap[p.code] && p.code !== 'ADD_ARN',
  );

  const lineDiscount = (
    code: string,
    gross: number,
  ): { discountLabel: string; discountAmount: number } => {
    const d = productDiscounts[code];
    if (!d || d.value <= 0) return { discountLabel: '', discountAmount: 0 };
    return {
      discountLabel: d.mode === 'percent' ? `${d.value}%` : inr(d.value),
      discountAmount: resolveLineDiscountAmount(d.mode, d.value, gross),
    };
  };

  const rows: DetailedQuotationRow[] = [];

  if (aumItem && setupQuote?.setupAmount != null) {
    const gross = setupQuote.setupAmount;
    const band = setupQuote.slab?.band ? ` (${setupQuote.slab.band})` : '';
    const years =
      productTerms[aumItem.code] ?? getDefaultTermYears(aumItem.code, { isAumBased: true });
    const { discountLabel, discountAmount } = lineDiscount(aumItem.code, gross);
    rows.push({
      name: `${aumItem.name}${band}`,
      duration: durationText(years),
      price: gross,
      discountLabel,
      discountAmount,
      net: Math.max(0, gross - discountAmount),
    });
  }

  flatItems.forEach((p) => {
    const gross = resolveProductLineTotal(
      p.code,
      p.price,
      customPrices,
      productTerms,
      renewalDiscountPercent,
    );
    const years = productTerms[p.code] ?? getDefaultTermYears(p.code);
    const { discountLabel, discountAmount } = lineDiscount(p.code, gross);
    rows.push({
      name: p.name,
      duration: durationText(years),
      price: gross,
      discountLabel,
      discountAmount,
      net: Math.max(0, gross - discountAmount),
    });
  });

  if (setupQuote && setupQuote.additionalArnAmount > 0) {
    rows.push({
      name: `Additional ARN x${setupQuote.additionalArnCount}`,
      duration: '',
      price: setupQuote.additionalArnAmount,
      discountLabel: '',
      discountAmount: 0,
      net: setupQuote.additionalArnAmount,
    });
  }

  const netSubtotal = setupQuote?.subtotal ?? rows.reduce((s, r) => s + r.net, 0);
  const totalDiscount =
    setupQuote?.discountAmount ?? rows.reduce((s, r) => s + r.discountAmount, 0);
  const gstPercentage = setupQuote?.gstRate ?? 18;
  const gstAmount = setupQuote?.gstAmount ?? round2((netSubtotal * gstPercentage) / 100);
  const totalPayable = setupQuote?.grandTotal ?? round2(netSubtotal + gstAmount);

  return {
    rows,
    totals: {
      grossSubtotal: round2(netSubtotal + totalDiscount),
      totalDiscount,
      netSubtotal,
      gstPercentage,
      gstAmount,
      totalPayable,
    },
    hasAum: Boolean(aumItem),
  };
}
